import { useCallback, useEffect, useState } from "react";

import { getMenuItemBadges } from "services/menuItemBadges/rest";
import { MenuItemBadgeDto } from "services/menuItemBadges/restModels";

export interface UseMenuItemBadgesResult {
    badges: MenuItemBadgeDto[];
    loading: boolean;
    error: boolean;
    setBadges: (badges: MenuItemBadgeDto[]) => void;
    refetch: () => void;
}

/*
* Справочник бейджей филиала: его читают экран бейджей и редактор позиции. Кешируем на уровне
* модуля (как useModifierSets), иначе каждый переход «меню → позиция → назад» тянул бы его
* заново. Правки экрана пишутся в кеш через setBadges, поэтому редактор позиции видит их
* без запроса.
* */
let cache: MenuItemBadgeDto[] | null = null;
let inFlight: Promise<MenuItemBadgeDto[] | null> | null = null;

const loadMenuItemBadges = (): Promise<MenuItemBadgeDto[] | null> => {
    if (cache) return Promise.resolve(cache);

    if (!inFlight) {
        inFlight = getMenuItemBadges()
            .then((result) => {
                if (result) cache = result;
                return result;
            })
            .finally(() => {
                inFlight = null;
            });
    }

    return inFlight;
};

export const useMenuItemBadges = (): UseMenuItemBadgesResult => {
    const [badges, setLocalBadges] = useState<MenuItemBadgeDto[]>(cache || []);
    const [loading, setLoading] = useState<boolean>(!cache);
    const [error, setError] = useState<boolean>(false);
    const [reloadKey, setReloadKey] = useState<number>(0);

    const setBadges = useCallback((next: MenuItemBadgeDto[]) => {
        cache = next;
        setLocalBadges(next);
    }, []);

    const refetch = useCallback(() => {
        cache = null;
        setReloadKey((key) => key + 1);
    }, []);

    useEffect(() => {
        // ignore-флаг: ответ после размонтирования не трогает стейт.
        let ignore = false;

        setLoading(true);
        setError(false);

        loadMenuItemBadges()
            .then((result) => {
                if (ignore) return;
                if (result) {
                    setLocalBadges(result);
                } else {
                    setError(true);
                    setLocalBadges([]);
                }
            })
            .catch(() => {
                if (ignore) return;
                setError(true);
                setLocalBadges([]);
            })
            .finally(() => {
                if (ignore) return;
                setLoading(false);
            });

        return () => {
            ignore = true;
        };
    }, [reloadKey]);

    return { badges, loading, error, setBadges, refetch };
};
